/**
 * The undefined-aware numeric figure — `src/primitives/`, design/09 §8 and §12.5.
 *
 * THE BEHAVIOUR: **undefined is rendered, not hidden.** An unmeasured value is visibly
 * unmeasured and never reads as a zero.
 *
 * Two chapters arrived at the same component independently and recorded the same
 * decision from different directions:
 *
 *   ch06, `docs/adr/0070`   a ratio over a zero denominator is UNDEFINED, not zero. An
 *                           option with no intake has no sell-through, and rendering
 *                           that as `0%` sorts it to the bottom of a ranked review pack
 *                           beside the genuine failures
 *   ch05, `docs/adr/0061`   `pub_exit_route_realisation` publishes a NULL row for every
 *                           route with no evidence. A screen rendering those as `0`
 *                           shows a route that recovered nothing rather than a route
 *                           nobody has measured — and reqs/05 §3.5 routes each lot to
 *                           the highest net realisation, so the difference is a stock
 *                           movement, not a presentation detail
 *
 * It is the same rule reqs/00 D8 states for the grid and `src/charts/` states for a
 * series: an absence is a declared state, and the distinction travels to the pixel.
 * Both chapters wrote the component locally and neither promoted it, because promotion
 * is a shared-ownership decision (`CLAUDE.md` §2.2) and a cross-feature import is a
 * build failure by design. `docs/adr/0012` is that decision.
 *
 * WHY `absent` IS REQUIRED: an absence has an owner. "No intake, so no rate" is a
 * different fact from "the feed did not deliver", and both are different from "we have
 * not measured this yet". A default would pick one silently, and the type's whole job
 * here is that the caller has to say which. There is no default and no fallback copy.
 */

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { formatCompactNumber, displayNumberFormat } from '../lib/format';
import { CellBar, ProportionBar, type BarTone, type CellBarSpec } from './bars';
import { AbsentWords } from './absent-words';
import { GHOST_MARK, useGhost } from './ghost';
import { AboutPopover } from './about-popover';
import { KpiLinkAbove, KpiLinkAnchor, useKpiLink } from './kpi-link';

/**
 * What an absent value MEANS. Required wherever a value may be absent — a numeric cell
 * cannot be constructed without it.
 */
export interface AbsentMeaning {
  /**
   * ICU key naming the absence — the WORDS the cell shows in place of the number («Nessuna riga»,
   * «Non confrontabile»), never a dash (ADR 9061). Keep it short: a cell cuts it at 24 characters
   * and carries the full words in `title`. G6 — never a literal.
   */
  readonly labelKey: string;
  /**
   * ICU key naming WHY the value is absent, where the caller can distinguish reasons.
   * Optional here and required by narrower cells that carry a reason vocabulary of
   * their own — see ch05's `UnscoredCell` and `docs/adr/0061`.
   */
  readonly reasonKey?: string;
}

// The em-dash is `GHOST_MARK` (`./ghost.tsx`): not copy, the same glyph in every locale. It is the
// GHOST's glyph alone (ADR 9013); an absence is written in words (`AbsentWords`, ADR 9061).

export interface MeasureProps {
  readonly labelKey: string;
  /** `null` is undefined — never zero. See the module note. */
  readonly value: number | null;
  readonly unitKey: string;
  /**
   * ICU arguments for `unitKey` — a unit that names a value the catalogue cannot know, such
   * as the run's currency code (`"{currency}"`). The same road as `actionTitleValues` (ADR 0093).
   */
  readonly unitValues?: Readonly<Record<string, string | number>>;
  readonly fractionDigits?: number;
  /** Show the value, a share, in percent with `fractionDigits` places (a Measure then draws no unit). */
  readonly percent?: boolean;
  /** REQUIRED — what `null` means here. There is no default. */
  readonly absent: AbsentMeaning;
  /**
   * How loud the number is.
   *
   * `dense` is `type.numeric` — 13px, the size of a grid cell — and is what every
   * `Measure` in the product rendered until 2026-09-24. `metric` is `type.metric`, whose
   * `$use` in `tokens/typography.json` is the words **"KPI tile values"** and which, when
   * measured that day, had exactly one consumer in the repository: a chart caption. The
   * headline number on a screen was the same size as the least important number on it.
   *
   * The default is still `dense`, deliberately. Flipping 146 call sites in one change
   * would restyle every screen in the product at once, unreviewed — a `Measure` in a
   * four-across band wants `metric` and one in a dense side panel does not, and which is
   * which is a judgement per screen. `KpiBand` and the converted screens pass `metric`;
   * the rest move as they are looked at. ADR 9006 §3.
   */
  readonly size?: 'dense' | 'metric';
  /**
   * A marker beside the label — the "Illustrative" tag on a figure that comes from a fixture
   * (audit v5b D-02: "Illustrative marked on every value from a fixture"). A node rather than a
   * flag, because the tag lives in `src/planning/` and this layer does not import upward.
   */
  readonly tag?: ReactNode;
  /**
   * Compact above 10,000 (`2.41M`), the exact figure in `title` — audit v6 rule 4, ADR 9013.
   * Off by default for the same reason `size` defaults to `dense`: a band of four headline
   * figures wants it, a dense side panel of exact counts does not, and that is a per-screen
   * call rather than one to make for 146 call sites at once. `fractionDigits` still governs a
   * figure at or below the threshold.
   */
  readonly compact?: boolean;
  /**
   * The part-of-a-whole bar under the figure (`ProportionBar`, spec v6b §1 block 2) — when the
   * figure IS a part of a whole: "140 of 2,230 candidates", "40.7% of doors".
   */
  readonly proportion?: {
    readonly value: number | null;
    readonly whole: number | null;
    readonly tone?: BarTone;
    readonly labelKey?: string;
    readonly labelValues?: Readonly<Record<string, string | number>>;
  };
  /**
   * What the figure really is — its definition, grain, source and limits (DEC-111, ADR 9047): an
   * (i) after the label opens it. A catalogue key; `descriptionValues` fills it with what the
   * screen read (the run, the as-of date). The census (`src/test/info-census.test.ts`) fails a
   * `Measure` on a registered screen without one.
   */
  readonly descriptionKey?: string;
  readonly descriptionValues?: Readonly<Record<string, string | number>>;
  /**
   * The list behind the figure (`docs/adr/9069`): the whole tile becomes a link to it, built with
   * the screen's own route helpers. The list must sum to this figure. The (i) stays outside the
   * link. Absent: the tile is what it always was.
   */
  readonly href?: string;
}

/**
 * One labelled number that may be undefined.
 *
 * design/09 §7.5 is explicit that a single KPI is a stat tile and not a dial, which is
 * why this is a figure with a value and a unit — the charting wrapper has no API for a
 * gauge at all (G2 test *c*). `absent.reasonKey` renders beneath the figure, where a
 * tile has room for a sentence.
 */
export function Measure({
  labelKey, value, unitKey, unitValues, fractionDigits = 0, absent, size = 'dense', tag, compact = false, proportion, percent = false,
  descriptionKey, descriptionValues, href,
}: MeasureProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  // A ghosted figure is a silhouette whatever it was handed — ghost is not zero (ADR 9013) — and opens nothing.
  const ghost = useGhost();
  const link = useKpiLink(ghost || value === null ? undefined : href);
  const shown = ghost ? null : value;
  const figure = shown === null ? null : formatFigure(shown, i18n.language, fractionDigits, compact, percent);

  // The absent branch takes the same size as the present one. An absence written small
  // beside a 28px figure would read as a lesser kind of answer, and "not measured" is not
  // a lesser answer — it is the finding (ADR 0098). It is now a word, so a long one wraps in
  // the tile rather than being cut: the tile has the room a table cell has not.
  const figureClass = size === 'metric'
    ? 'text-metric font-numeric font-medium tabular-nums'
    : 'text-numeric font-numeric tabular-nums';

  return (
    <div className={`flex flex-col gap-1${link.rootClass === '' ? '' : ` ${link.rootClass}`}`}>
      <span className="flex flex-wrap items-center gap-2">
        <span {...link.labelProps} className={size === 'metric' ? 'text-micro text-ink-secondary' : 'text-caption text-ink-secondary'}>
          {t(labelKey)}
        </span>
        {descriptionKey !== undefined && (
          <KpiLinkAbove link={link}>
            <AboutPopover
              aboutKeys={[descriptionKey]}
              title={t(labelKey)}
              size="sm"
              {...(descriptionValues === undefined ? {} : { values: descriptionValues })}
            />
          </KpiLinkAbove>
        )}
        {tag}
      </span>
      <KpiLinkAnchor link={link}>
        <span {...link.valueProps} className="flex items-baseline gap-1">
          {figure === null ? (
            ghost ? (
              <span className={`${figureClass} text-ink-secondary`} aria-label={t('ghost.value')}>
                {GHOST_MARK}
              </span>
            ) : (
              // An absence is written in words, at the figure's size, never a dash (ADR 9061). The
              // reason is the line beneath, where a tile has room for it.
              <span className={`${figureClass} text-ink-secondary`} title={t(absent.labelKey)}>
                {t(absent.labelKey)}
              </span>
            )
          ) : (
            <span className={`${figureClass} text-ink-primary`} {...(figure.title === null ? {} : { title: figure.title })}>
              {figure.text}
            </span>
          )}
          {/* No unit on an absence — audit v5 N-03. "— feeds" put a dash at figure size beside a
              word, and it read as a rule across the tile rather than as a value; the absence is now
              itself a word and a unit beside it would read as a sentence. A unit
              qualifies a quantity; there is no quantity to qualify, and the reason beneath says
              what is missing. A GHOST keeps its unit, muted (spec v6b tab 5, "— proposals"):
              the silhouette's job is to show what will stand there, and the strip above the
              screen — not a reason under each tile — says why nothing does yet. */}
          {figure !== null && !percent && <span className="text-caption text-ink-secondary">{t(unitKey, unitValues ?? {})}</span>}
          {ghost && <span className="text-caption text-ink-secondary">{t(unitKey, unitValues ?? {})}</span>}
        </span>
      </KpiLinkAnchor>
      {!ghost && figure === null && absent.reasonKey !== undefined ? (
        <span className="text-caption text-ink-secondary">{t(absent.reasonKey)}</span>
      ) : null}
      {proportion === undefined ? null : (
        <ProportionBar
          value={proportion.value}
          whole={proportion.whole}
          {...(proportion.tone === undefined ? {} : { tone: proportion.tone })}
          {...(proportion.labelKey === undefined ? {} : { labelKey: proportion.labelKey })}
          {...(proportion.labelValues === undefined ? {} : { labelValues: proportion.labelValues })}
        />
      )}
    </div>
  );
}

/** One figure for display: exact with `fractionDigits`, or compact with the exact in `title`. */
function formatFigure(
  value: number, locale: string, fractionDigits: number, compact: boolean, percent = false,
): { readonly text: string; readonly title: string | null } {
  // A share in percent (usability audit F5): 0,852 reads «85,2 %», never the bare fraction.
  if (percent) {
    return {
      text: displayNumberFormat(locale, {
        style: 'percent', minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits,
      }).format(value),
      title: null,
    };
  }
  if (compact) {
    const figure = formatCompactNumber(value, locale);
    if (figure.compacted) return { text: figure.text, title: figure.exact };
  }
  return {
    text: displayNumberFormat(locale, {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(value),
    title: null,
  };
}

export interface NumericCellProps {
  readonly value: number | null;
  readonly fractionDigits?: number;
  /** Show the value, a share, in percent with `fractionDigits` places (a Measure then draws no unit). */
  readonly percent?: boolean;
  /** REQUIRED — what `null` means in this column. There is no default. */
  readonly absent: AbsentMeaning;
  /** Compact above 10,000 with the exact figure in `title` (audit v6 rule 4, ADR 9013). */
  readonly compact?: boolean;
  /**
   * A bar beside the number, against the column's `max` — audit v6 V6-08, "la tabella si
   * legge come un grafico senza perdere il numero". `reference` makes it the double variant.
   * `BarCell` is this cell with `bar` required.
   */
  readonly bar?: CellBarSpec;
  /**
   * The column that matters — audit v5b D-03, rendered per brief part 1 §1 (pkgC2): the cell on
   * `surface-emphasis` with its figure at 600. `'total'` is the same column in the totals row:
   * `surface-emphasis-head` (the header's step) with the total's 700. A `DataTable` column with
   * `emphasis` sets it; see `docs/adr/9028`.
   */
  readonly emphasis?: boolean | 'total';
}

/**
 * A table cell carrying a number that may be undefined.
 *
 * §12.5 — the numeric face, right-aligned and column-aligned, and it keeps `text-end`
 * and `tabular-nums` in the absent branch too so the column still scans. The absence's
 * WORDS (`absent.labelKey`) when there is nothing to show — never a dash, never a blank and
 * never a zero (ADR 9061). `absent.reasonKey`, where the caller has one, is said ONCE per table:
 * inside a `DataTable` (or any `AbsenceNotes`) the table lists each distinct «label: reason» under
 * itself and the cell points at its note with `aria-describedby` (client rule 2,
 * `docs/adr/9028`). Outside one it rides beside the words as a badge.
 *
 * With `bar`, the number keeps its end-aligned column and the bar takes the rest of the cell,
 * so a column of bar cells still scans as a column of numbers.
 */
export function NumericCell({
  value, fractionDigits = 0, absent, compact = false, bar, emphasis = false, percent = false,
}: NumericCellProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const ghost = useGhost();
  const shown = ghost ? null : value;
  const reference = bar?.reference;
  const referenceFigure = ghost || reference === null || reference === undefined
    ? null
    : formatFigure(reference, i18n.language, fractionDigits, compact);
  let content: React.JSX.Element;
  if (shown === null) {
    // A silhouette keeps the glyph (ADR 9013); an absence is written in words (ADR 9061), and
    // inside a table that states its reasons once (`AbsenceNotes`, which every `DataTable` is)
    // the reason goes under the table while the cell points at it.
    content = ghost
      ? <span aria-label={t('ghost.value')}>{GHOST_MARK}</span>
      : <AbsentWords absent={absent} />;
  } else {
    const figure = formatFigure(shown, i18n.language, fractionDigits, compact, percent);
    content = (
      <span {...(figure.title === null ? {} : { title: figure.title })}>
        {referenceFigure === null
          ? figure.text
          : t('barCell.ofReference', { value: figure.text, reference: referenceFigure.text })}
      </span>
    );
  }
  // On the emphasised column's tint secondary ink falls under AA (4.4:1); its words take the label grey.
  const tone = shown === null ? (emphasis === false ? ' text-ink-secondary' : ' text-ink-label') : '';
  const size = emphasis === 'total'
    ? 'text-body bg-surface-emphasis-head font-bold'
    : emphasis ? 'text-body bg-surface-emphasis font-semibold' : 'text-numeric';
  if (bar === undefined) {
    return <td className={`font-numeric ${size} text-end tabular-nums${tone}`}>{content}</td>;
  }
  return (
    <td className={`font-numeric ${size} tabular-nums${tone}`}>
      <span className="flex items-center gap-2">
        <CellBar value={shown} bar={bar} />
        <span className="min-w-12 shrink-0 text-end">{content}</span>
      </span>
    </td>
  );
}

export interface BarCellProps extends NumericCellProps {
  readonly bar: CellBarSpec;
}

/**
 * `NumericCell` with its bar — the name the audit uses (V6-08). A separate export so a
 * column that must carry a bar says so in its type rather than by remembering a prop.
 */
export function BarCell(props: BarCellProps): React.JSX.Element {
  return <NumericCell {...props} />;
}
